import { InteractionsEngagementLogger } from './loggers/InteractionsEngagementLogger';
import { InteractionsDatadogLogger } from './loggers/InteractionsDatadogLogger';
import { BaseInteractionsLogger } from './loggers/BaseInteractionsLogger';
import { EventInteractionWithContext, EventInteractions, PLACE_EVENT_INTERACTION, TrackableElement } from './types';

/**
 * Manages batching and periodic logging of event card interactions
 *
 * This tracker:
 * - Listens for PLACE_EVENT_INTERACTION events
 * - Batches interactions to reduce API calls
 * - Sends batched data every 1.25 seconds
 * - Deduplicates certain events (impressions, mounted)
 *
 */
export class EventsInteractionsTracker {
  private readonly LOG_PERIODICITY_MILLISECONS = 1250;

  /** DOM element to listen for events on */
  private readonly element: TrackableElement;

  private readonly interactionsLoggers: BaseInteractionsLogger<unknown>[];
  private interactions: EventInteractionWithContext[] = [];
  private readonly interactions_history: Set<string> = new Set();

  /**
   * Creates a new EventsInteractionsTracker
   *
   * @param element - DOM element to listen for events on
   * @param interactionsLoggers - Array of loggers to process interactions
   */
  constructor(
    element: TrackableElement,
    interactionsLoggers: BaseInteractionsLogger<unknown>[] = [
      new InteractionsEngagementLogger(),
      new InteractionsDatadogLogger(),
    ],
  ) {
    this.element = element;
    this.interactionsLoggers = interactionsLoggers;
  }

  /**
   * Starts listening for PLACE_EVENT_INTERACTION events
   *
   * This method should be called once when the tracker is initialized.
   * It sets up the event listener that captures interactions.
   */
  listen() {
    this.element.addEventListener(PLACE_EVENT_INTERACTION, (event: CustomEvent<EventInteractionWithContext>) => {
      const detail = event.detail;
      const interactionUniqueId = `${detail.id}-${detail.place.placementId}-${detail.action}`;

      if (uniqueInteractions.includes(detail.action) && this.interactions_history.has(interactionUniqueId)) {
        return;
      }

      this.interactions_history.add(interactionUniqueId);
      this.interactions.push(detail);
    });
  }

  /**
   * Starts the periodic batching and logging process
   *
   * This method:
   * - Sets up a timer to send batched interactions
   * - Sets up a beforeunload handler to send any remaining interactions
   *
   * Should be called after listen() to start the tracking process.
   */
  track() {
    setInterval(() => {
      this.logStoredInteractions();
    }, this.LOG_PERIODICITY_MILLISECONS);

    this.element.addEventListener('beforeunload', () => this.logStoredInteractions());
  }

  /**
   * Processes and sends all stored interactions to the loggers
   *
   * This method:
   * - Sends interactions in batches of 10
   * - Clears the interactions buffer after sending
   */
  private logStoredInteractions() {
    const logsLimit = 10;
    for (let i = 0; i < this.interactions.length; i += logsLimit) {
      const logsBlock = this.interactions.slice(i, i + logsLimit);

      this.interactionsLoggers.forEach((logger) => {
        logger.run(logsBlock);
      });
    }
    this.interactions = [];
  }
}

// This interactions should be only logged once
const uniqueInteractions = [EventInteractions.Impression, EventInteractions.Mounted];
